import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import useFetchEndPoint from '../hooks/useFetchEndPoint';
import { initHotjar } from '../helpers/hotjar';

export enum Role {
  student = 'student',
  teacher = 'teacher',
  manager = 'manager',
  staff = 'staff',
  organization_user = 'organization_user',
}

export enum PagePermission {
  content = 'content',
  guides = 'guides',
  index = 'index',
  license = 'license',
  licenses = 'licenses',
  licenses_program = 'licenses_program',
  module = 'module',
  study_group = 'study_group',
  unit = 'unit',
}

export interface User {
  pk: string;
  is_authenticated: boolean;
  is_demo: boolean;
  demo_end?: number;
  demos_to_ask?: {
    demo: string;
    ask: {
      name: string;
      key: string;
      default: any;
      peviousValue?: any;
      value?: any;
      type: string;
    }[];
  }[];
  name: string;
  full_name: string;
  email: string;
  roles: Role[];
  managed?: string;
  pages: Record<PagePermission, boolean>;
  pinnings: string;
  chalkups: string;
  pending: boolean;
  has_email: boolean;
  show_experimental_features: boolean;
  chat_host: string;
  user_actions: string;
  token: string;
  session_id: string;
  username: string;
  first_name: string;
  last_name: string;
  show_intro: boolean;
  content_feedback: string;
  namespaces: string[];
  flags: string[];
  has_wrong_username: boolean;
  has_wrong_email: boolean;
  editor_admin: boolean;
  is_hijacked?: boolean;
  hijacker?: { pk: string; name: string };
}

export const AuthContext = React.createContext<User | null>(null);

const AuthProvider: React.FC = ({ children }) => {
  const user = useFetchEndPoint<User>('/api/v1/user/');
  const wasHijackedRef = useRef(false);

  useEffect(() => {
    if (user) {
      initHotjar(user);
      wasHijackedRef.current = Boolean(user.is_hijacked);
    }
  }, [user]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!wasHijackedRef.current) return;

      axios
        .get<User>('/api/v1/user/', {
          withCredentials: true,
          params: { _t: Date.now() },
        })
        .then(({ data }) => {
          const wasHijacked = wasHijackedRef.current;
          const isHijacked = Boolean(data.is_hijacked);
          wasHijackedRef.current = isHijacked;

          if (wasHijacked && !isHijacked) {
            window.location.reload();
          }
        })
        .catch(() => {});
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return <AuthContext.Provider value={user} children={children} />;
};

export default AuthProvider;
